import { useEffect, useState } from 'react';
import { AppShell, type TabId } from './components/layout/AppShell';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { LoginScreen } from './features/auth/LoginScreen';
import { SchichtplanView } from './features/schichtplan/SchichtplanView';
import { VorschauView } from './features/vorschau/VorschauView';
import { VerfuegbarkeitenView } from './features/verfuegbarkeiten/VerfuegbarkeitenView';
import { MitarbeiterView } from './features/mitarbeiter/MitarbeiterView';
import { BaustellenView } from './features/baustellen/BaustellenView';
import { EinstellungenView } from './features/einstellungen/EinstellungenView';
import { useLogout, useSession } from './lib/queries';
import { useLiveSync } from './lib/useLiveSync';

const TAB_STORAGE_KEY = 'egger.tab';
const VALID_TABS: TabId[] = [
  'vorschau',
  'schichtplan',
  'verfuegbarkeiten',
  'mitarbeiter',
  'baustellen',
  'einstellungen',
];

export function App() {
  const session = useSession();
  const logout = useLogout();
  const user = session.data?.user ?? null;
  const connection = useLiveSync(Boolean(user));
  const [tab, setTab] = useState<TabId>(readStoredTab);

  useEffect(() => {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab);
  }, [tab]);

  if (session.isLoading) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Schichtplanung wird geladen …
      </div>
    );
  }

  if (!user) return <LoginScreen />;

  return (
    <AppShell
      user={user}
      activeTab={tab}
      onTabChange={setTab}
      connection={connection}
      onLogout={() => logout.mutate()}
    >
      {/* Eigene Fehlergrenze je Ansicht: ein Problem im Plan darf die
          Stammdatenpflege nicht mit umreißen. */}
      <ErrorBoundary key={tab}>
        {tab === 'vorschau' && <VorschauView />}
        {tab === 'schichtplan' && <SchichtplanView user={user} />}
        {tab === 'verfuegbarkeiten' && <VerfuegbarkeitenView user={user} />}
        {tab === 'mitarbeiter' && <MitarbeiterView user={user} />}
        {tab === 'baustellen' && <BaustellenView user={user} />}
        {tab === 'einstellungen' && <EinstellungenView user={user} />}
      </ErrorBoundary>
    </AppShell>
  );
}

function readStoredTab(): TabId {
  const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
  return VALID_TABS.includes(stored as TabId) ? (stored as TabId) : 'schichtplan';
}
